import { useQuery } from '@tanstack/react-query';
import { Cloud, Server } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';
import { money } from '@/types/offer';

export type ServerContract = {
  id: string;
  name: string;
  domain: string | null;
  server_type: 'byos' | 'hetzner';
  active: boolean;
  since: string;
  monthly_price: string | null;
  currency: string | null;
  free_until: string | null;
};

function onDay(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
}

export type ServerContractsPayload = {
  servers: ServerContract[];
  currency: string | null;
  tier: string;
  tier_name: string;
  plan: string | null;
  plan_name: string | null;
};

export function useServerContracts(orgId?: string) {
  return useQuery<ServerContractsPayload>({
    queryKey: ['server-contracts', orgId ?? 'active'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance(orgId ? { 'X-Org-Id': orgId } : {});
      return (await api.get('/billing/server-contracts/')).data as ServerContractsPayload;
    },
    retry: false,
  });
}

export function ServerContractCard({ server, owner }: { server: ServerContract; owner: string }) {
  const cloud = server.server_type === 'hetzner';
  const Icon = cloud ? Cloud : Server;
  const price = server.monthly_price && server.currency
    ? `${money(server.monthly_price, server.currency)} a month`
    : cloud ? 'Price not set yet' : 'No licence fee';
  return (
    <Card data-testid={`server-contract-${server.id}`}>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="flex items-start gap-3">
            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${cloud ? 'bg-sky-100 text-sky-700' : 'bg-slate-100 text-slate-700'}`}>
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-base">{server.name}</CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">
                {cloud ? 'Private cloud server hosted by BitSwan' : `${owner} own server, licensed to run Bailey`}
                {server.domain ? ` · ${server.domain}` : ''} · since {onDay(server.since)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={server.active ? 'bg-emerald-500/15 text-emerald-700' : 'bg-amber-500/15 text-amber-800'}>
              {server.active ? 'Running' : cloud ? 'Being set up' : 'Not registered yet'}
            </Badge>
            <span className="text-sm font-medium" data-testid={`server-contract-price-${server.id}`}>{price}</span>
          </div>
        </div>
      </CardHeader>
      {server.free_until && server.monthly_price && (
        <CardContent className="pt-0">
          <p className="text-sm text-muted-foreground">Free during the trial, billed from {onDay(server.free_until)} onwards.</p>
        </CardContent>
      )}
    </Card>
  );
}

export function ServerContractsSection({ orgId, owner = 'Your' }: { orgId?: string; owner?: string }) {
  const { data } = useServerContracts(orgId);
  const servers = data?.servers ?? [];
  if (servers.length === 0) return null;
  return (
    <section className="space-y-3" data-testid="server-contracts">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Servers</h3>
        {data?.tier_name && (
          <span className="text-xs text-muted-foreground" data-testid="server-contracts-tier">
            {data.plan_name ? `${data.tier_name} · ${data.plan_name}` : data.tier_name} prices
          </span>
        )}
      </div>
      {servers.map((server) => <ServerContractCard key={server.id} server={server} owner={owner} />)}
    </section>
  );
}
